import React, { useMemo, useState } from 'react';
import { Plus, Calendar, TrendingUp, Star, Clock, Utensils, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    useMyDailyMeals,
    useMyDailyPointsSummary,
    useMyJournalStats,
    useMyMealStats,
    useCreateMealLog,
    useDeleteMealLog,
} from '@/hooks/useJournalApi';
import type { CreateMealLogRequest } from '@/types/journal-api';
import {
    formatShortDay,
    fromLocalDateStr,
    isTodayLocalDateStr,
    todayLocalDateStr,
} from '@/lib/date';
import { MealLogForm } from '@/components/journal/MealLogForm';
import { useFoodCategories } from '@/services/categories';
import { mealCategoryQuantities } from '@/lib/meal-categories';

export const MealTracking: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState(todayLocalDateStr());
    const [showMealForm, setShowMealForm] = useState(false);

    // Real API data
    const { data: dailyMeals = [], isLoading: mealsLoading } = useMyDailyMeals(selectedDate);
    const { data: pointsSummary, isLoading: summaryLoading } =
        useMyDailyPointsSummary(selectedDate);
    const { data: journalStats } = useMyJournalStats();
    // The streak on this page counts logged meals, not journal entries (#216).
    const { data: mealStats } = useMyMealStats();

    // Mutations
    const createMealLog = useCreateMealLog();
    const deleteMealLog = useDeleteMealLog();

    const isLoading = mealsLoading || summaryLoading;

    // The day cards follow the date picker, so they only say Today when it is
    // today (#371).
    const isToday = isTodayLocalDateStr(selectedDate);
    const shortDay = formatShortDay(selectedDate);

    // Quantities per meal, keyed by meal id. The category list is a name
    // fallback for meals whose category relation is missing a row the
    // categoryPoints JSON still records (#220).
    const { data: foodCategories = [] } = useFoodCategories();
    const categoryNames = useMemo(
        () => new Map(foodCategories.map((category) => [category.id, category.name])),
        [foodCategories],
    );
    const quantitiesByMeal = useMemo(
        () =>
            new Map(
                dailyMeals.map((meal) => [meal.id, mealCategoryQuantities(meal, categoryNames)]),
            ),
        [dailyMeals, categoryNames],
    );

    const formatTime = (timeString: string) => {
        return new Date(timeString).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    const getMealTypeColor = (mealType: string) => {
        const colors: Record<string, string> = {
            BREAKFAST: 'bg-brand-fill-orange text-brand-fill-ink',
            LUNCH: 'bg-brand-fill-green text-brand-fill-ink',
            DINNER: 'bg-brand-fill-lime text-brand-fill-ink',
            SNACK: 'bg-brand-fill-yellow text-brand-fill-ink',
        };
        return colors[mealType] || 'bg-muted text-foreground';
    };

    // Both tabs post the payload MealLogForm builds; this page adds nothing of
    // its own to it (#205).
    const handleCreateMealLog = (data: CreateMealLogRequest) => {
        createMealLog.mutate(data, {
            onSuccess: () => {
                setShowMealForm(false);
            },
        });
    };

    const handleDeleteMeal = (mealId: string) => {
        if (window.confirm('Are you sure you want to delete this meal?')) {
            deleteMealLog.mutate(mealId);
        }
    };

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading your meal tracking data...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Same full-screen treatment as the Journal tab rather than this page's old
    // modal, so the form renders at an identical width in both places (#205).
    if (showMealForm) {
        return (
            <div className="min-h-screen bg-linear-to-br from-indigo-50 via-white to-purple-50">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <MealLogForm
                        onSubmit={handleCreateMealLog}
                        onCancel={() => setShowMealForm(false)}
                        initialDate={selectedDate}
                        isSubmitting={createMealLog.isPending}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">Meal Tracking</h1>
                    <p className="text-muted-foreground mt-1">
                        Track your meals and earn Light Points
                    </p>
                </div>
                <Button onClick={() => setShowMealForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Log Meal
                </Button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                {isToday ? "Today's Points" : `Points on ${shortDay}`}
                            </p>
                            <p className="text-2xl font-bold text-foreground">
                                {pointsSummary?.totalPoints || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-fill-yellow">
                            <Star className="h-6 w-6 text-brand-fill-ink" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">Current Streak</p>
                            <p className="text-2xl font-bold text-foreground">
                                {mealStats?.currentStreak || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">days</p>
                        </div>
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-fill-green">
                            <TrendingUp className="h-6 w-6 text-brand-fill-ink" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                {isToday ? 'Meals Today' : `Meals on ${shortDay}`}
                            </p>
                            <p className="text-2xl font-bold text-foreground">
                                {pointsSummary?.totalMeals || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-fill-orange">
                            <Utensils className="h-6 w-6 text-brand-fill-ink" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">Total Entries</p>
                            <p className="text-2xl font-bold text-foreground">
                                {journalStats?.totalEntries || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-fill-lime">
                            <Calendar className="h-6 w-6 text-brand-fill-ink" />
                        </div>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Today's Meals */}
                <div className="lg:col-span-2">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">
                                Meals for{' '}
                                {fromLocalDateStr(selectedDate).toLocaleDateString('en-GB', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                })}
                            </h2>
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="border border-input rounded px-3 py-1 text-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            {dailyMeals.map((meal) => (
                                <div
                                    key={meal.id}
                                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-2">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-medium ${getMealTypeColor(meal.mealType)}`}
                                            >
                                                {meal.mealType.toLowerCase()}
                                            </span>
                                            <div className="flex items-center text-sm text-muted-foreground">
                                                <Clock className="w-4 h-4 mr-1" />
                                                {formatTime(meal.timeEaten)}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {meal.totalPoints > 0 && (
                                                <div className="flex items-center text-sm font-medium text-brand-text-blue">
                                                    <Star className="w-4 h-4 mr-1 fill-current" />
                                                    {meal.totalPoints} pts
                                                </div>
                                            )}
                                            <button
                                                onClick={() => handleDeleteMeal(meal.id)}
                                                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                                                title="Delete meal"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <h3 className="font-medium text-foreground mb-1">
                                            {meal.customMealName || meal.recipe?.name || 'Meal'}
                                        </h3>
                                        {(quantitiesByMeal.get(meal.id)?.length ?? 0) > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {quantitiesByMeal.get(meal.id)?.map((cat) => (
                                                    <span
                                                        key={cat.id}
                                                        className="text-xs bg-brand-header-wash text-brand-text-blue px-2 py-0.5 rounded"
                                                    >
                                                        {cat.name}
                                                        {cat.servings === null
                                                            ? ''
                                                            : `: ${cat.servings}`}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {meal.notes && (
                                            <p className="text-sm text-muted-foreground mt-2">
                                                {meal.notes}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {dailyMeals.length === 0 && (
                                <div className="text-center py-12">
                                    <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-muted-foreground mb-2">
                                        No meals logged for this day
                                    </h3>
                                    <p className="text-muted-foreground mb-4">
                                        Start tracking your meals to earn Light Points!
                                    </p>
                                    <Button onClick={() => setShowMealForm(true)}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Log Your First Meal
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Category Summary */}
                <div className="space-y-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4">Categories</h3>
                        {pointsSummary?.categoryTotals &&
                        pointsSummary.categoryTotals.length > 0 ? (
                            <div className="space-y-3">
                                {pointsSummary.categoryTotals.map((cat) => (
                                    <div
                                        key={cat.categoryId}
                                        className="flex items-center justify-between"
                                    >
                                        <span className="text-sm text-foreground">
                                            {cat.categoryName}
                                        </span>
                                        <div className="flex items-center space-x-2">
                                            <span
                                                className={`text-sm font-medium ${cat.isGoalMet ? 'text-brand-text-green' : 'text-muted-foreground'}`}
                                            >
                                                {cat.servings}
                                                {cat.target ? ` / ${cat.target}` : ''}{' '}
                                                {cat.servings === 1 ? 'serving' : 'servings'}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {cat.points} pts
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No category data yet. Log meals to see your progress!
                            </p>
                        )}
                    </Card>

                    <Card className="p-6">
                        {/* mealTypeTotals sums points, not meals — the old "38 meals"
                            was the same points-labelled-as-a-count slip as #210. */}
                        <h3 className="text-lg font-semibold mb-4">Points by Meal Type</h3>
                        {pointsSummary?.mealTypeTotals ? (
                            <div className="space-y-3">
                                {Object.entries(pointsSummary.mealTypeTotals).map(
                                    ([type, points]) => (
                                        <div
                                            key={type}
                                            className="flex items-center justify-between"
                                        >
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-medium ${getMealTypeColor(type)}`}
                                            >
                                                {type.toLowerCase()}
                                            </span>
                                            <span className="text-sm font-medium text-foreground">
                                                {points} pts
                                            </span>
                                        </div>
                                    ),
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">No meals logged yet.</p>
                        )}
                    </Card>

                    {journalStats && (
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold mb-4">Journal Stats</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Total Points
                                    </span>
                                    <span className="text-sm font-medium text-brand-text-blue">
                                        {journalStats.totalPoints}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Completion Rate
                                    </span>
                                    <span className="text-sm font-medium text-brand-text-blue">
                                        {Math.round(journalStats.completionRate)}%
                                    </span>
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-muted-foreground">Progress</span>
                                        <span className="text-muted-foreground">
                                            {journalStats.completedEntries}/
                                            {journalStats.totalEntries}
                                        </span>
                                    </div>
                                    <div className="w-full bg-brand-bar-track rounded-full h-2">
                                        <div
                                            className="bg-brand-bar-fill border border-brand-bar-edge rounded-full h-2"
                                            style={{ width: `${journalStats.completionRate}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};
